import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const ls = {
  get: (k, d) => {
    try {
      const v = localStorage.getItem(k);
      return v ? JSON.parse(v) : d;
    } catch {
      return d;
    }
  },
  set: (k, v) => {
    try {
      localStorage.setItem(k, JSON.stringify(v));
    } catch {}
  },
};

export function StoreProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [cart, setCart] = useState(() => ls.get("svs_cart", []));
  const [wishlist, setWishlist] = useState(() => ls.get("svs_wishlist", []));
  const [b2bMode, setB2bMode] = useState(() => ls.get("svs_b2b", false));
  const [currency, setCurrency] = useState(() => ls.get("svs_currency", "INR"));

  useEffect(() => {
    let cancelled = false;
    api
      .get("/auth/me")
      .then((r) => {
        if (!cancelled) setUser(r.data.user);
      })
      .catch(() => {
        if (!cancelled) setUser(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onExpired = () => {
      setUser((prev) => {
        if (prev) toast.error("Your session expired. Please sign in again.");
        return false;
      });
    };
    window.addEventListener("svs:session-expired", onExpired);
    return () => {
      window.removeEventListener("svs:session-expired", onExpired);
    };
  }, []);

  useEffect(() => {
    ls.set("svs_cart", cart);
  }, [cart]);
  useEffect(() => {
    ls.set("svs_wishlist", wishlist);
  }, [wishlist]);
  useEffect(() => {
    ls.set("svs_b2b", b2bMode);
  }, [b2bMode]);
  useEffect(() => {
    ls.set("svs_currency", currency);
  }, [currency]);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const r = await api.post("/auth/register", payload);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
    setCart([]);
    setServerCart(null);
    toast.success("Logged out");
  }, []);

  const [serverCart, setServerCart] = useState(null);
  const applyServer = useCallback((data) => {
    setServerCart(data);
    setCart(data.items.map((i) => ({ ...i, slug: i.slug, qty: i.quantity, price: i.unit_price, images: [i.image], item_id: i.id })));
  }, []);
  const cartError = (err, fallback) => toast.error(formatApiError(err?.response?.data?.detail) || fallback);
  const refreshCart = useCallback(() => api.get("/cart").then((r) => applyServer(r.data)).catch(() => {}), [applyServer]);

  useEffect(() => {
    let cancelled = false;
    if (!user) { if (user === false) setServerCart(null); return undefined; }
    const local = ls.get("svs_cart", []).filter((i) => !i.item_id);
    const run = local.length
      ? api.post("/cart/merge", { items: local.map((i) => ({ slug: i.slug, variant_id: i.variant_id || null, quantity: i.qty || 1 })) })
      : api.get("/cart");
    run.then((r) => { if (!cancelled) applyServer(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [user, applyServer]);

  const addToCart = useCallback(
    async (product, qty = 1, variant = null, opts = {}) => {
      if (user) {
        try {
          const r = await api.post("/cart/items", { slug: product.slug, variant_id: variant?.id || null, quantity: qty, b2b: !!opts.b2b });
          applyServer(r.data);
          toast.success(`${product.name} added to cart${opts.b2b ? " at wholesale price" : ""}`);
        } catch (err) { cartError(err, "Could not add to cart"); }
        return;
      }
      setCart((prev) => {
        const existing = prev.find((i) => i.slug === product.slug);
        if (existing) return prev.map((i) => (i.slug === product.slug ? { ...i, qty: i.qty + qty } : i));
        return [...prev, { ...product, qty, variant_id: variant?.id || null }];
      });
      toast.success(`${product.name} added to cart`);
    },
    [user, applyServer]
  );

  const removeFromCart = useCallback(async (slug) => {
    const item = cart.find((i) => i.slug === slug);
    if (user && item?.item_id) {
      try { applyServer((await api.delete(`/cart/items/${item.item_id}`)).data); } catch (err) { cartError(err, "Could not remove item"); }
      return;
    }
    setCart((prev) => prev.filter((i) => i.slug !== slug));
  }, [user, cart, applyServer]);

  const updateQty = useCallback(async (slug, qty) => {
    const item = cart.find((i) => i.slug === slug);
    if (user && item?.item_id) {
      try { applyServer((await api.put(`/cart/items/${item.item_id}`, { quantity: Math.max(1, qty) })).data); } catch (err) { cartError(err, "Could not update quantity"); }
      return;
    }
    setCart((prev) => prev.map((i) => (i.slug === slug ? { ...i, qty: Math.max(1, qty) } : i)));
  }, [user, cart, applyServer]);

  const clearCart = useCallback(async () => {
    if (user) {
      try { applyServer((await api.delete("/cart")).data); } catch (err) { cartError(err, "Could not clear cart"); }
      return;
    }
    setCart([]);
  }, [user, applyServer]);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.find((i) => i.slug === product.slug);
      if (exists) {
        toast("Removed from wishlist");
        return prev.filter((i) => i.slug !== product.slug);
      }
      toast.success("Added to wishlist");
      return [...prev, product];
    });
  }, []);

  const inWishlist = useCallback(
    (slug) => wishlist.some((i) => i.slug === slug),
    [wishlist]
  );

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cart.reduce((s, i) => s + (i.item_id ? i.price : i.price) * i.qty, 0);

  const value = {
    user,
    setUser,
    login,
    register,
    logout,
    cart,
    serverCart,
    refreshCart,
    addToCart,
    removeFromCart,
    updateQty,
    clearCart,
    cartCount,
    cartTotal,
    wishlist,
    toggleWishlist,
    inWishlist,
    b2bMode,
    setB2bMode,
    currency,
    setCurrency,
    formatApiError,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
